import {useCallback, useEffect, useState} from "react";
import {Clock, MessageSquare, Trash2, User} from "lucide-react";
import {createAdminUser, deleteAdminUser, getAdminUser, listAdminUsers, updateAdminUser, type AdminUserRow} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminToggleField, AdminRowTrailing} from "./ui";

const ROLE_OPTS = [
  {v: 0, l: "Administrator"},
  {v: 1, l: "Employee"},
  {v: 2, l: "SEO operator"}
];

export function UsersView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const [items, setItems] = useState<AdminUserRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState({nume: "", email: "", telefon: "", parola: "", rol: 1, sms: 0});
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminUsers({page, pageSize: 20, search: searchQuery || undefined}).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
      setSmsEnabled(r.smsEnabled);
    }).catch((e: Error) => setErr(e.message));
  }, [page, searchQuery]);
  useEffect(() => { load(); }, [load]);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }

  async function openItem(id: number) {
    setOpenId(id);
    setModalOpen(true);
    const u = await getAdminUser(id);
    setForm({nume: u.nume, email: u.email, telefon: u.telefon, parola: "", rol: u.rol, sms: u.sms});
  }

  function openNew() {
    setOpenId("new");
    setForm({nume: "", email: "", telefon: "", parola: "", rol: 1, sms: 0});
    setModalOpen(true);
  }

  async function saveUser() {
    try {
      if(openId === "new") {
        if(!form.parola) { adminToast.error("Password is required."); return; }
        await createAdminUser(form);
        adminToast.success("User added.");
      } else if(typeof openId === "number") {
        await updateAdminUser(openId, form);
        adminToast.success("User saved.");
      }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Could not save user");
    }
  }

  function removeUser(id: number) {
    confirm("Delete user?", async () => {
      await deleteAdminUser(id);
      closeModal(false);
      load();
      adminToast.success("User deleted.");
    });
  }

  function userFormFields(showRole: boolean, passwordPlaceholder?: string) {
    return (
      <div className="admin-detail-panel__form">
        <AdminField label="Name">
          <Input placeholder="Name" value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} />
        </AdminField>
        <AdminField label="Email">
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
        </AdminField>
        <AdminField label="Phone">
          <Input placeholder="Phone" value={form.telefon} onChange={(e) => setForm({...form, telefon: e.target.value})} />
        </AdminField>
        <AdminField label="Password">
          <Input type="password" placeholder={passwordPlaceholder || "Password"} value={form.parola} onChange={(e) => setForm({...form, parola: e.target.value})} />
        </AdminField>
        {showRole ? (
          <AdminField label="Rol">
            <Select value={String(form.rol)} onValueChange={(v) => setForm({...form, rol: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </AdminField>
        ) : null}
        {smsEnabled ? (
          <AdminToggleField
            title="SMS notifications"
            description="Allow SMS alerts for this admin user."
            checked={form.sms === 1}
            onCheckedChange={(v) => setForm({...form, sms: v ? 1 : 0})}
          />
        ) : null}
      </div>
    );
  }

  const showRole = openId === "new" || (typeof openId === "number" && openId !== 1);
  const passwordPlaceholder = openId === "new" ? undefined : "New password (optional)";
  const openUser = typeof openId === "number" ? items.find((u) => u.id === openId) : null;

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title="Users"
      description="Manage admin accounts"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Users"}]}
      actions={<AdminAddButton onClick={openNew} label="Add user" />}
    >
      {confirmDialog}
      <AdminModernTable
        items={items}
        getId={(u) => u.id}
        getTitle={(u) => u.nume}
        getSubtitle={(u) => `${u.email} — ${u.rolLabel}`}
        getMeta={(u) => (
          <>
            {u.isOnline ? <AdminBadge variant="secondary">Activ</AdminBadge> : null}
            {u.neverAccessed ? <AdminBadge variant="danger">Account never accessed</AdminBadge> : null}
            {u.smsEnabled && u.sms === 1 ? <AdminBadge variant="secondary"><MessageSquare className="h-3 w-3" /></AdminBadge> : null}
            {u.sessionMinutes > 0 ? <AdminBadge variant="secondary"><Clock className="h-3 w-3" /> {u.sessionMinutes} min</AdminBadge> : null}
            {u.activLabel ? <AdminBadge variant="outline">{u.activLabel}</AdminBadge> : null}
          </>
        )}
        getSearchText={(u) => adminSearchText(u.id, u.nume, u.email, u.telefon, u.rolLabel, u.activLabel, u.sessionMinutes, u.sms)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={(u) => openItem(u.id)}
        emptyTitle="No users"
        emptyDescription="Add a new user or adjust search."
        searchPlaceholder="Search name, email, phone, role..."
        getRowActions={(u) => (
          <AdminRowTrailing
            onDelete={u.protected ? undefined : () => removeUser(u.id)}
            deleteLabel="Delete user"
          />
        )}
      />
      <AdminEditModal
        open={modalOpen && openId !== null}
        onOpenChange={closeModal}
        title={openId === "new" ? "Add user" : "Edit user"}
        icon={User}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveUser()}>Save</Button>
            {openUser && !openUser.protected ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeUser(openUser.id)}><Trash2 className="h-4 w-4" /> Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        {userFormFields(showRole, passwordPlaceholder)}
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
